import { useZmClient } from "@components/hooks/useZmClient";
import { GlowPostApi, NftCollection } from "@glow/shared";
import { LuxButton } from "@lux/atoms/components/LuxButton";
import { LuxInputField } from "@lux/atoms/components/LuxInput";
import { LuxInputLabel } from "@lux/atoms/components/LuxInputLabel";
import { LuxModalContainer } from "@lux/atoms/components/LuxModal";
import { useBoolean } from "@lux/atoms/hooks/useBoolean";
import { ModalProps, useModalState } from "@lux/atoms/hooks/useModalState";
import { callWithToast } from "@utils/toast";
import { ZmClient } from "@utils/ZmClient";
import { Form, Formik } from "formik";
import { LuxSubmitButton } from "@lux/atoms/components/LuxSubmitButton";

export const NftCollectionAdminRow = ({
  collection,
  refresh,
}: {
  collection: NftCollection.BasicInfo;
  refresh: () => Promise<unknown>;
}) => {
  const refreshing = useBoolean();
  const refreshingTraits = useBoolean();
  const missingItemsModal = useModalState();
  const rawJsonModal = useModalState();
  const updateModal = useModalState();

  return (
    <div className="flex-center my-3 overflow-auto">
      <LuxButton
        label={"Refresh Collection"}
        onClick={async () => {
          refreshing.setTrue();
          await callWithToast(
            async () => {
              await ZmClient.post("/solana/nft/refresh-nft-collection", {
                nft_collection_api_id: collection.api_id as any,
              });

              // await TaskUtils.waitForTaskResult({ task_id, ZmClient });
              await refresh();
            },
            {
              loading: "Refreshing...",
              success: "Refreshed...",
              error: "Problem refreshing!!",
            },
          );

          refreshing.setFalse();
        }}
        size={"small"}
      />

      <LuxButton
        label={"Refresh Metadata"}
        className={"ml-3"}
        onClick={async () => {
          refreshing.setTrue();
          await callWithToast(
            async () => {
              await ZmClient.post(
                "/solana/nft/refresh-nft-collection-metadata",
                {
                  nft_collection_api_id: collection.api_id as any,
                },
              );

              // await TaskUtils.waitForTaskResult({ task_id, ZmClient });
              await refresh();
            },
            {
              loading: "Refreshing...",
              success: "Refreshed...",
              error: "Problem refreshing!!",
            },
          );

          refreshing.setFalse();
        }}
        size={"small"}
      />

      <LuxButton
        label={"Refresh Traits Summary"}
        onClick={async () => {
          refreshingTraits.setTrue();
          await callWithToast(
            async () => {
              await ZmClient.post("/solana/nft/refresh-nft-collection-traits", {
                nft_collection_api_id: collection.api_id as any,
              });
              await refresh();
            },
            {
              loading: "Refreshing...",
              success: "Refreshed...",
              error: "Problem refreshing!!",
            },
          );

          refreshingTraits.setFalse();
        }}
        size={"small"}
        className={"ml-3"}
      />

      <LuxButton
        label={"Show Missing Items"}
        onClick={missingItemsModal.onShow}
        className={"ml-3"}
        size={"small"}
      />

      <MissingItemsModal
        nftCollectionApiId={collection.api_id}
        {...missingItemsModal}
      />

      <LuxButton
        label={"Show JSON"}
        onClick={rawJsonModal.onShow}
        className={"ml-3"}
        size={"small"}
      />
      <RawJsonModal data={collection} {...rawJsonModal} />

      <LuxButton
        label={"Update Attributes"}
        onClick={updateModal.onShow}
        className={"ml-3"}
        size={"small"}
      />

      <UpdateModal
        collection={collection}
        onUpdate={async () => {
          await refresh();
        }}
        {...updateModal}
      />
    </div>
  );
};

const RawJsonModal = ({ show, onHide, data }: ModalProps & { data: any }) => {
  return (
    <LuxModalContainer onHide={onHide} title={"Missing Items"}>
      {show && (
        <div>
          <pre>{JSON.stringify(data, null, 2)}</pre>
        </div>
      )}
    </LuxModalContainer>
  );
};

const MissingItemsModal = ({
  show,
  onHide,
  nftCollectionApiId,
}: ModalProps & { nftCollectionApiId: string }) => {
  const { data } = useZmClient({
    path: "/solana/explore/get-missing-collection-items",
    args: {
      nft_collection_api_id: nftCollectionApiId,
    },
    pause: !show,
  });
  return (
    <LuxModalContainer onHide={onHide} title={"Missing Items"}>
      {show && (
        <div>
          {data && (
            <div>
              <pre>Max: {JSON.stringify(data.max_id, null, 2)}</pre>
              <pre>Min: {JSON.stringify(data.min_id, null, 2)}</pre>
              <pre>Null IDs: {JSON.stringify(data.null_names, null, 2)}</pre>
              <pre>Missing: {JSON.stringify(data.missing_ids, null, 2)}</pre>
              <pre>Dupes: {JSON.stringify(data.duplicate_ids, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </LuxModalContainer>
  );
};

const UpdateModal = ({
  show,
  onHide,
  collection,
  onUpdate,
}: ModalProps & {
  collection: NftCollection.BasicInfo;
  onUpdate: () => Promise<unknown>;
}) => {
  return (
    <LuxModalContainer onHide={onHide} title={"Update Collection"}>
      {show && (
        <div>
          <Formik
            initialValues={{
              magic_eden_symbol: collection.magic_eden_symbol,
              image_url: collection.image_url,
            }}
            onSubmit={async (values) => {
              await callWithToast(
                async () => {
                  const params: GlowPostApi["/solana/nft/update-collection"]["request"] =
                    {
                      solana_nft_collection_api_id: collection.api_id,
                      ...values,
                    };

                  await ZmClient.post("/solana/nft/update-collection", params);
                  await onUpdate();
                  onHide();
                },
                { error: "Problem updating collection..." },
              );
            }}
          >
            <Form>
              <div className="mb-3 highlight-label">
                <LuxInputLabel text={"Image"} />
                TODO
                {/*<ImageField name={"image_url"} />*/}
              </div>

              <LuxInputField
                label={"Magic Eden Symbol"}
                name={"magic_eden_symbol"}
                className={"mb-3"}
              />

              <LuxSubmitButton label={"Update"} className={"mt-4"} />
            </Form>
          </Formik>
        </div>
      )}
    </LuxModalContainer>
  );
};
